import React from 'react'
import { Table, Tag, Typography, Space, Popover } from 'antd'
import { StarOutlined, PercentageOutlined, InfoCircleOutlined } from '@ant-design/icons'

const { Text } = Typography

interface PricingTier {
  quantity: string // "1-4", "5-9", "10+"
  unitPrice: number
  customerPrice: number
  volumePrice: number
  savings?: number
  savingsPercent?: number
}

interface QuantityBreakDisplayProps {
  pricingTiers?: PricingTier[]
  basePrice: number
  hasCustomerDiscount?: boolean
}

const QuantityBreakDisplay: React.FC<QuantityBreakDisplayProps> = ({
  pricingTiers,
  basePrice,
  hasCustomerDiscount
}) => {
  if (!pricingTiers || pricingTiers.length === 0) {
    return null
  }

  // Show all tiers, but only display if there are multiple tiers (indicating volume discounts)
  if (pricingTiers.length <= 1) {
    return null
  }

  // Use the first tier's unitPrice as the base for comparison (retail price)
  const retailPrice = pricingTiers[0]?.unitPrice || basePrice

  const columns = [
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity: string) => (
        <Text strong style={{ fontSize: '14px' }}>
          {quantity}
        </Text>
      )
    },
    {
      title: 'Price per Unit',
      dataIndex: 'volumePrice',
      key: 'volumePrice',
      width: 140,
      render: (price: number) => (
        <Text style={{ color: '#52c41a', fontWeight: 'bold', fontSize: '15px' }}>
          ${price.toFixed(2)}
        </Text>
      )
    },
    {
      title: 'Savings',
      key: 'savings',
      width: 130,
      render: (_: any, record: PricingTier) => {
        const savings = retailPrice - record.volumePrice
        const savingsPercent = savings > 0 ? (savings / retailPrice) * 100 : 0
        if (savings > 0.01) { // Show savings if more than 1 cent
          return (
            <Space direction="vertical" size={2} style={{ lineHeight: 1.4 }}>
              <Text type="success" strong style={{ fontSize: '13px' }}>
                ${savings.toFixed(2)} off
              </Text>
              <Tag color="success" style={{ margin: 0, fontSize: '11px', padding: '2px 6px' }}>
                {savingsPercent.toFixed(0)}% OFF
              </Tag>
            </Space>
          )
        }
        return <Text type="secondary">-</Text>
      }
    }
  ]

  const popoverContent = (
    <div style={{ width: 400, padding: '4px 0' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <PercentageOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
          <Text strong style={{ fontSize: '15px', color: '#262626' }}>
            Volume Discounts
          </Text>
        </div>
        <Table
          dataSource={pricingTiers}
          columns={columns}
          pagination={false}
          size="small"
          rowKey="quantity"
          style={{ fontSize: '13px' }}
          bordered={false}
          showHeader={true}
        />
        {hasCustomerDiscount && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e8e8e8' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <StarOutlined style={{ color: '#faad14', marginRight: 6 }} />
              Prices include your customer discount
            </Text>
          </div>
        )}
      </Space>
    </div>
  )

  return (
    <Popover
      content={popoverContent}
      title={null}
      trigger="hover"
      placement="top"
      overlayStyle={{ maxWidth: 450 }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
          marginTop: 8
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f0f0'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          View volume discounts
        </Text>
      </div>
    </Popover>
  )
}

export default QuantityBreakDisplay

